import { AttachmentType } from '@flowx/designer/modules/ai-builder/types/composer-file.types'
import {
  asNumberMap,
  asOptionalNumber,
  isRecord,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'

/** Where the harness has got with one of the turn's files, before it answers, in order:
 * `uploading`, `indexing`, then `ready` or `failed`. */
export enum HarnessAttachmentState {
  Uploading = 'uploading',
  Indexing = 'indexing',
  Ready = 'ready',
  Failed = 'failed',
}

/** One `attachment` frame: one file's state, streamed once per change before any token. */
export type HarnessAttachmentStatus = {
  attachmentId: string
  fileName: string
  state: HarnessAttachmentState
  /** False for an image read only directly, or a file the harness stopped waiting for. Only on the
   * final state. */
  searchable: boolean
  failureReason: string | null
}

/** What files a turn may carry, as `GET /sessions/{id}` publishes it. Deployment-wide:
 * every session answers the same one. `types` maps an admitted mime to its maximum bytes. */
export type HarnessAttachmentsPolicy = {
  enabled: boolean
  maxFilesPerTurn: number
  maxFilesPerSession: number
  types: Readonly<Record<string, number>>
}

export function toAttachmentsPolicy(value: unknown): HarnessAttachmentsPolicy | null {
  if (!isRecord(value) || typeof value['enabled'] !== 'boolean') {
    return null
  }

  const maxFilesPerTurn = asOptionalNumber(value['max_files_per_turn'])
  const maxFilesPerSession = asOptionalNumber(value['max_files_per_session'])

  return maxFilesPerTurn === undefined || maxFilesPerSession === undefined
    ? null
    : {
        enabled: value['enabled'],
        maxFilesPerTurn,
        maxFilesPerSession,
        types: asNumberMap(value['types']),
      }
}

const ATTACHMENT_STATES: ReadonlySet<string> = new Set(Object.values(HarnessAttachmentState))

/** A wire state the client knows, or null. */
export function toAttachmentState(value: unknown): HarnessAttachmentState | null {
  return isAttachmentState(value) ? value : null
}

function isAttachmentState(value: unknown): value is HarnessAttachmentState {
  return typeof value === 'string' && ATTACHMENT_STATES.has(value)
}

/** One file on a turn body. Exactly these keys: the harness refuses any other. */
export type HarnessAttachmentRef = { temp_file_id: string; file_name: string; mime_type: string }

/** A file as the thread shows it under the message that carried it. */
export type HarnessSentFile = {
  name: string
  /** Null on a message read back from history: the harness keeps no size for it. */
  size: number | null
  /** Null on a message read back from history: only files this tab sent are hashed. */
  hash: string | null
  mime: string
  tempFileId: string | null
  /** The workspace its temp file went up in; a turn made from another one cannot read it. */
  workspaceId?: string
  /** The harness's id for the file, once its first `attachment` frame named it. */
  attachmentId: string | null
  /** The document-center entry it became: on history reads, or named by the session read after
   * its turn. */
  storeEntryUuid: string | null
  /** Where the harness has got with it; null until its first `attachment` frame. */
  state: HarnessAttachmentState | null
  searchable: boolean
  failureReason: string | null
}

/** A file's state in the document center, as app-manager reports it. */
export enum MakerFileState {
  Processing = 'PROCESSING',
  Ready = 'READY',
  Failed = 'FAILED',
  /** The stored file is gone. */
  NotFound = 'NOT_FOUND',
}

/** One file of `POST /api/maker-files/list`, as app-manager sends it. */
export type MakerFile = {
  storeEntryUuid: string
  storeUuid: string
  name: string
  mimeType: string
  /** Bytes, when the upload or the harness handed app-manager a size. */
  size?: number
  state: MakerFileState
  failureReason?: string
  conversationId: string
  appId?: string
  createdDate: string
}

/** One page of a conversation's files. */
export type MakerFilePage = { total: number; content: readonly MakerFile[] }

/** What opening a file needs: its stored entry, once known, and what it is. */
export type OpenableFile = Pick<HarnessSentFile, 'storeEntryUuid' | 'name' | 'mime'>

/** The one mark a sent file draws: under its message, and on its panel row. */
export enum SentFileMark {
  /** Sent, and no `attachment` frame about it yet. */
  Attached = 'attached',
  Processing = 'processing',
  Ready = 'ready',
  /** Ready but not searchable: an image read directly, or a file the harness stopped waiting for. */
  NotSearchable = 'not_searchable',
  Failed = 'failed',
  /** Taken out of the conversation: no longer there to open. */
  Removed = 'removed',
}

/** How a file opens from the thread or the panel. */
export enum FileOpening {
  /** In a new tab: PDFs, and BPMN as its XML. */
  Tab = 'tab',
  /** In a preview over the page that the maker closes. */
  Preview = 'preview',
  Download = 'download',
}

/** What the image preview shows. */
export type FilePreview = { name: string; url: string }

/** Where a file the maker asked to take out of the conversation has got to. */
export enum ConversationFileRemoval {
  None = 'none',
  Removing = 'removing',
  Removed = 'removed',
  Failed = 'failed',
}

/** The removal of one file, keyed in the store by the file's row id. */
export type ConversationFileRemovalState = {
  status: ConversationFileRemoval
  /** Why the removal failed, as the row shows it. */
  error: string | null
}

/** One row of the conversation's file panel: a document-center file, with where its removal has
 * got to. */
export type ConversationFile = {
  /** The stored entry: what the content and delete routes name. */
  id: string
  name: string
  /** Null when app-manager lists none for it. */
  size: number | null
  mime: string
  type: AttachmentType
  /** When it was stored, as the message that carried it was sent. */
  sentAt: number
  mark: SentFileMark
  failureReason: string | null
  /** Only a file whose reading failed is read again; one whose store is gone is not. */
  retryable: boolean
  removal: ConversationFileRemovalState
}
